import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/site/Reveal";

/**
 * The real enquiry form, posting to the API that emails the estate. Replaces
 * the old mailto: link — a visitor who has just seen a price should be able to
 * ask for it without leaving the page or opening a mail client.
 */

const FIELD =
  "w-full border-0 border-b border-hairline bg-transparent px-1 py-3 font-body text-base text-ink placeholder:text-stone/50 focus:border-ink focus:outline-none focus:ring-0";

export const InquiryForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", guests: "", message: "", company: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error(t.form.required);
      return;
    }
    setSending(true);
    try {
      const res = await fetch("/api/inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          guests: form.guests ? Number(form.guests) : undefined,
          message: form.message.trim() || undefined,
          company: form.company || undefined, // honeypot
          lang,
        }),
      });
      if (res.status === 429) {
        toast.error(t.form.rateLimited);
      } else if (!res.ok) {
        toast.error(t.form.error);
      } else {
        setSent(true);
        toast.success(t.form.success);
      }
    } catch {
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="cerere" data-testid="inquiry-section" className="bg-bone">
      <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-12 px-5 py-24 md:grid-cols-2 md:px-10 md:py-32">
        <div>
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-rust">
              {t.form.overline}
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-display text-5xl font-medium uppercase leading-[0.9] tracking-tight text-ink md:text-6xl">
              {t.form.title}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-md font-body text-lg leading-relaxed text-stone">
              {t.contact.body}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <a
              href={`mailto:${t.contact.email}`}
              className="mt-8 inline-block border-b border-ink pb-0.5 font-body text-sm text-ink transition-colors hover:border-rust hover:text-rust"
            >
              {t.contact.email}
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          {sent ? (
            <div
              data-testid="inquiry-success"
              className="flex h-full flex-col items-start justify-center gap-4 border border-olive/40 bg-olive/5 p-10"
            >
              <span className="font-display text-3xl uppercase text-olive">✓</span>
              <p className="font-body text-lg leading-relaxed text-ink">{t.form.success}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="inquiry-form" className="flex flex-col gap-6">
              <input
                className={FIELD} required placeholder={t.form.name}
                value={form.name} onChange={set("name")} autoComplete="name"
                data-testid="inquiry-name"
              />
              <input
                className={FIELD} required type="email" placeholder={t.form.email}
                value={form.email} onChange={set("email")} autoComplete="email"
                data-testid="inquiry-email"
              />
              <div className="grid grid-cols-2 gap-6">
                <input
                  className={FIELD} type="tel"
                  placeholder={`${t.form.phone} (${t.form.optional})`}
                  value={form.phone} onChange={set("phone")} autoComplete="tel"
                />
                <input
                  className={FIELD} type="number" min="1" max="50"
                  placeholder={`${t.form.guests} (${t.form.optional})`}
                  value={form.guests} onChange={set("guests")}
                />
              </div>
              <textarea
                className={`${FIELD} resize-none`} rows={4}
                placeholder={t.form.messagePlaceholder}
                value={form.message} onChange={set("message")}
                data-testid="inquiry-message"
              />

              {/* Honeypot: hidden from people, irresistible to bots. */}
              <input
                type="text" name="company" tabIndex={-1} autoComplete="off"
                aria-hidden="true" value={form.company} onChange={set("company")}
                className="absolute left-[-9999px] h-0 w-0 opacity-0"
              />

              <button
                type="submit" disabled={sending} data-testid="inquiry-submit"
                className="group mt-2 inline-flex items-center justify-center gap-3 self-start rounded-full bg-ink px-9 py-4 font-body text-[13px] font-semibold uppercase tracking-[0.18em] text-bone transition-colors duration-500 hover:bg-olive disabled:opacity-60"
              >
                {sending ? t.form.sending : t.form.submit}
                <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
